import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import {
  Search,
  Image,
  Users,
  LayoutDashboard,
  FileText,
  Settings,
  ArrowRight,
  Clock,
  Sparkles,
  XCircle,
  ExternalLink,
  CornerDownLeft,
} from 'lucide-react'
import { useArtworkSearch } from '@/features/admin/hooks/useAdminSearch'
import { artworkApi } from '@/features/artworks/api/artworkApi'
import { cn } from '@/lib/utils'

interface QuickAction {
  id: string
  name: string
  description: string
  icon: React.ReactNode
  shortcut?: string
  action: () => void
  category: 'navigation' | 'action'
}

// Trigger button component
interface CommandPaletteTriggerProps {
  className?: string
}

export function CommandPaletteTrigger({ className }: CommandPaletteTriggerProps) {
  const [open, setOpen] = useState(false)

  // Keyboard shortcut to open (⌘+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={cn(
          'flex items-center gap-2 h-9 px-3 text-sm text-muted-foreground',
          'bg-background border border-border rounded-lg',
          'hover:border-border/80 hover:bg-accent/50 transition-all duration-150',
          'shadow-sm cursor-pointer',
          'lg:w-64 lg:justify-start',
          className
        )}
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="hidden lg:block flex-1 text-left">Search artworks...</span>
        <kbd className="hidden lg:flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium font-sans text-muted-foreground bg-muted border border-border rounded">
          <span>⌘</span>K
        </kbd>
      </button>

      {open && <CommandPaletteModal onClose={() => setOpen(false)} />}
    </>
  )
}

// Modal component
interface CommandPaletteModalProps {
  onClose: () => void
}

function CommandPaletteModal({ onClose }: CommandPaletteModalProps) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const { data: searchData, isLoading } = useArtworkSearch(
    { keyword: query || undefined },
    { start: 0, limit: 6 },
    query.length >= 2
  )

  // Quick actions for navigation
  const quickActions: QuickAction[] = [
    {
      id: 'dashboard',
      name: 'Dashboard',
      description: 'View system overview and pending approvals',
      icon: <LayoutDashboard className="h-4 w-4" />,
      shortcut: 'G D',
      action: () => navigate('/app/dashboard'),
      category: 'navigation',
    },
    {
      id: 'artworks',
      name: 'Browse Artworks',
      description: 'View and manage all artworks',
      icon: <Image className="h-4 w-4" />,
      shortcut: 'G A',
      action: () => navigate('/app/artworks'),
      category: 'navigation',
    },
    {
      id: 'artists',
      name: 'Artists',
      description: 'Manage artist profiles and portfolios',
      icon: <Users className="h-4 w-4" />,
      shortcut: 'G R',
      action: () => navigate('/app/artists'),
      category: 'navigation',
    },
    {
      id: 'tasks',
      name: 'Task Queue',
      description: 'View and manage processing tasks',
      icon: <FileText className="h-4 w-4" />,
      shortcut: 'G T',
      action: () => navigate('/app/tasks'),
      category: 'navigation',
    },
    {
      id: 'users',
      name: 'User Admin',
      description: 'Manage user accounts and permissions',
      icon: <Settings className="h-4 w-4" />,
      shortcut: 'G U',
      action: () => navigate('/app/users'),
      category: 'navigation',
    },
  ]

  // Filter quick actions based on query
  const filteredActions = query.length > 0
    ? quickActions.filter(action =>
        action.name.toLowerCase().includes(query.toLowerCase()) ||
        action.description.toLowerCase().includes(query.toLowerCase())
      )
    : quickActions

  // Calculate total items for keyboard navigation
  const artworkResults = searchData?.data || []
  const totalItems = filteredActions.length + artworkResults.length + (searchData && searchData.total > 6 ? 1 : 0)

  // ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Focus input when opened
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 10)
  }, [])

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => Math.min(prev + 1, totalItems - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        handleSelect(selectedIndex)
        break
    }
  }, [selectedIndex, totalItems])

  // Scroll selected item into view
  useEffect(() => {
    const selectedElement = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`)
    selectedElement?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex])

  const handleSelect = useCallback((index: number) => {
    // First check quick actions
    if (index < filteredActions.length) {
      filteredActions[index].action()
      onClose()
      return
    }

    // Then artworks
    const artworkIndex = index - filteredActions.length
    if (artworkIndex < artworkResults.length) {
      navigate(`/app/artworks/${artworkResults[artworkIndex].id}`)
      onClose()
      return
    }

    // "View all results" option
    if (searchData && searchData.total > 6 && artworkIndex === artworkResults.length) {
      navigate(`/app/artworks?q=${encodeURIComponent(query)}`)
      onClose()
    }
  }, [filteredActions, artworkResults, searchData, navigate, query, onClose])

  const content = (
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center pt-[12vh]"
      onClick={onClose}
      style={{ animation: 'fadeIn 150ms ease-out' }}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-xl"
        style={{ WebkitBackdropFilter: 'blur(20px)' }}
      />
      
      {/* Main container */}
      <div
        className={cn(
          'relative w-full max-w-[680px] max-h-[70vh] mx-4',
          'bg-white dark:bg-zinc-900',
          'border border-zinc-200 dark:border-zinc-700',
          'rounded-2xl shadow-2xl',
          'flex flex-col overflow-hidden'
        )}
        onClick={e => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        style={{ animation: 'scaleIn 200ms cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        {/* Search header */}
        <div className="border-b border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center gap-3 px-5 py-4">
            <Search className="w-5 h-5 text-zinc-400 shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => {
                setQuery(e.target.value)
                setSelectedIndex(0)
              }}
              placeholder="Search artworks, artists, or actions..."
              className={cn(
                'flex-1 text-[17px] font-normal tracking-tight',
                'text-zinc-900 dark:text-zinc-100',
                'bg-transparent border-none outline-none',
                'placeholder:text-zinc-400'
              )}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            {query && (
              <button
                onClick={() => {
                  setQuery('')
                  inputRef.current?.focus()
                }}
                className="p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 rounded transition-colors"
              >
                <XCircle className="h-4 w-4" />
              </button>
            )}
            <kbd className="px-2 py-1 text-[11px] font-medium text-zinc-500 bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-600 rounded">
              esc
            </kbd>
          </div>
        </div>

        {/* Results */}
        <div ref={listRef} className="flex-1 overflow-y-auto p-2">
          {/* Quick Actions */}
          {filteredActions.length > 0 && (
            <div className="mb-2">
              <div className="flex items-center gap-2 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                <span>Quick Actions</span>
              </div>
              {filteredActions.map((action, index) => (
                <div
                  key={action.id}
                  data-index={index}
                  onClick={() => {
                    action.action()
                    onClose()
                  }}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all relative',
                    selectedIndex === index
                      ? 'bg-amber-50 dark:bg-amber-900/20'
                      : 'hover:bg-zinc-100 dark:hover:bg-zinc-800'
                  )}
                >
                  {selectedIndex === index && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-amber-500 rounded-r" />
                  )}
                  <div className={cn(
                    'flex items-center justify-center w-9 h-9 rounded-lg shrink-0',
                    'bg-gradient-to-br from-zinc-100 to-zinc-200 dark:from-zinc-700 dark:to-zinc-800',
                    'border border-zinc-200 dark:border-zinc-600',
                    'text-zinc-500 dark:text-zinc-400'
                  )}>
                    {action.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                      {action.name}
                    </div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                      {action.description}
                    </div>
                  </div>
                  {action.shortcut && (
                    <div className="flex gap-1 shrink-0">
                      {action.shortcut.split(' ').map((key, i) => (
                        <kbd key={i} className="min-w-[22px] h-[22px] px-1.5 flex items-center justify-center text-[11px] font-medium text-zinc-500 bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-600 rounded">
                          {key}
                        </kbd>
                      ))}
                    </div>
                  )}
                  {selectedIndex === index && (
                    <CornerDownLeft className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Artwork Results */}
          {query.length >= 2 && (
            <div className="mb-2">
              <div className="flex items-center gap-2 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                <Image className="h-3.5 w-3.5 text-blue-500" />
                <span>Artworks</span>
                {isLoading && <span className="ml-auto text-[10px] font-medium text-amber-500 animate-pulse">Searching...</span>}
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center gap-3 py-8 text-sm text-zinc-500">
                  <div className="w-4 h-4 border-2 border-zinc-200 dark:border-zinc-600 border-t-amber-500 rounded-full animate-spin" />
                  <span>Searching artworks...</span>
                </div>
              ) : artworkResults.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-sm text-zinc-500">
                  No artworks found for "{query}"
                </div>
              ) : (
                <>
                  {artworkResults.map((artwork, idx) => {
                    const index = filteredActions.length + idx
                    return (
                      <div
                        key={artwork.id}
                        data-index={index}
                        onClick={() => {
                          navigate(`/app/artworks/${artwork.id}`)
                          onClose()
                        }}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all relative',
                          selectedIndex === index
                            ? 'bg-amber-50 dark:bg-amber-900/20'
                            : 'hover:bg-zinc-100 dark:hover:bg-zinc-800'
                        )}
                      >
                        {selectedIndex === index && (
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-amber-500 rounded-r" />
                        )}
                        <div className="w-11 h-11 rounded-lg overflow-hidden shrink-0 bg-zinc-100 dark:bg-zinc-800">
                          <img
                            src={artworkApi.getArtworkImageUrl(artwork.id)}
                            alt={artwork.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Prevent infinite loop by checking if we're already showing the fallback
                              if (e.currentTarget.src.includes('placeholder-image')) {
                                return
                              }
                              e.currentTarget.src = '/placeholder-image.svg'
                            }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                            {artwork.name}
                          </div>
                          <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                            by {artwork.artist?.artisticName ||
                              `${artwork.artist?.firstName} ${artwork.artist?.lastName}`}
                          </div>
                        </div>
                        <ArrowRight className={cn(
                          'w-3.5 h-3.5 text-zinc-400 shrink-0 transition-all',
                          selectedIndex === index ? 'opacity-100' : 'opacity-0 -translate-x-1'
                        )} />
                        {selectedIndex === index && (
                          <CornerDownLeft className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                        )}
                      </div>
                    )
                  })}
                  
                  {/* View all results */}
                  {searchData && searchData.total > 6 && (
                    <div
                      data-index={filteredActions.length + artworkResults.length}
                      onClick={() => {
                        navigate(`/app/artworks?q=${encodeURIComponent(query)}`)
                        onClose()
                      }}
                      className={cn(
                        'flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl cursor-pointer transition-all',
                        'text-sm font-medium text-zinc-500',
                        selectedIndex === filteredActions.length + artworkResults.length
                          ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600'
                          : 'hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-700'
                      )}
                    >
                      <ExternalLink className="h-4 w-4" />
                      <span>View all {searchData.total} results</span>
                      {selectedIndex === filteredActions.length + artworkResults.length && (
                        <CornerDownLeft className="w-3.5 h-3.5 text-amber-500 ml-2" />
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Hint when no query */}
          {query.length === 0 && filteredActions.length === quickActions.length && (
            <div className="flex items-center justify-center gap-2 py-5 mt-2 text-sm text-zinc-400 border-t border-dashed border-zinc-200 dark:border-zinc-700">
              <Clock className="h-4 w-4" />
              <span>Start typing to search artworks, artists, and more...</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-6 px-5 py-3 border-t border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50">
          <div className="flex items-center gap-1.5 text-xs text-zinc-500">
            <kbd className="min-w-[20px] h-5 px-1 flex items-center justify-center text-[11px] font-medium bg-white dark:bg-zinc-700 border border-zinc-200 dark:border-zinc-600 rounded shadow-sm">
              ↑↓
            </kbd>
            <span>Navigate</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-zinc-500">
            <kbd className="min-w-[20px] h-5 px-1 flex items-center justify-center text-[11px] font-medium bg-white dark:bg-zinc-700 border border-zinc-200 dark:border-zinc-600 rounded shadow-sm">
              ↵
            </kbd>
            <span>Select</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-zinc-500">
            <kbd className="min-w-[20px] h-5 px-1.5 flex items-center justify-center text-[11px] font-medium bg-white dark:bg-zinc-700 border border-zinc-200 dark:border-zinc-600 rounded shadow-sm">
              esc
            </kbd>
            <span>Close</span>
          </div>
        </div>
      </div>

      {/* Inline keyframes */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.96) translateY(-10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  )

  return createPortal(content, document.body)
}
